import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/query-client";
import { Dialog, DialogPortal, DialogOverlay, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sparkles, Loader2 } from "lucide-react";

interface ClaimAIEmailDraftModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  claimId: string;
  onDraftGenerated: (subject: string, body: string) => void;
  currentEmailBody?: string;
}

export function ClaimAIEmailDraftModal({
  open,
  onOpenChange,
  claimId,
  onDraftGenerated,
  currentEmailBody,
}: ClaimAIEmailDraftModalProps) {
  const { toast } = useToast();

  const [aiRecipientType, setAiRecipientType] = useState("adjuster");
  const [aiEmailPurpose, setAiEmailPurpose] = useState("update");
  const [aiEmailContext, setAiEmailContext] = useState("");
  const [isGeneratingAIDraft, setIsGeneratingAIDraft] = useState(false);
  const [aiMode, setAiMode] = useState<'generate' | 'reword' | 'complete'>('generate');
  const [existingText, setExistingText] = useState('');

  // Pre-populate existingText from currentEmailBody when mode changes
  useEffect(() => {
    if ((aiMode === 'reword' || aiMode === 'complete') && currentEmailBody) {
      setExistingText(currentEmailBody);
    }
  }, [aiMode]);

  const handleGenerateAIDraft = async () => {
    setIsGeneratingAIDraft(true);
    try {
      const response = await apiRequest("POST", `/api/claims/${claimId}/draft-email`, {
        recipientType: aiRecipientType,
        purpose: aiEmailPurpose,
        context: aiEmailContext.trim() || undefined,
        mode: aiMode,
        existingText: aiMode !== 'generate' ? existingText : undefined,
      });
      const draft = await response.json();

      onDraftGenerated(draft.subject || draft.title || "", draft.content || "");
      onOpenChange(false);

      toast({
        title: "Draft Generated",
        description: "AI email draft has been populated into the email form.",
      });
    } catch (error: any) {
      toast({
        title: "Draft Generation Failed",
        description: error.message || "Failed to generate AI email draft.",
        variant: "destructive",
      });
    } finally {
      setIsGeneratingAIDraft(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogPortal>
        <DialogOverlay className="z-[59]" />
        <DialogPrimitive.Content
          className={cn(
            "fixed left-[50%] top-[50%] z-[60] grid w-[75vw] max-w-5xl translate-x-[-50%] translate-y-[-50%] gap-4 border bg-background p-6 shadow-lg duration-200",
            "data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
            "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%] sm:rounded-lg"
          )}
        >
        <DialogHeader>
          <DialogTitle className="flex items-center">
            <Sparkles className="h-5 w-5 mr-2" />
            AI Email Draft Assistant
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {/* Mode selector */}
          <div>
            <Label>Mode</Label>
            <Select value={aiMode} onValueChange={(v) => setAiMode(v as any)}>
              <SelectTrigger data-testid="select-ai-mode">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="generate">Generate New Draft</SelectItem>
                <SelectItem value="reword">Reword My Text</SelectItem>
                <SelectItem value="complete">Complete Partial Draft</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Existing text for reword/complete modes */}
          {(aiMode === 'reword' || aiMode === 'complete') && (
            <div>
              <Label>{aiMode === 'reword' ? 'Text to Reword' : 'Draft to Complete'}</Label>
              <Textarea
                value={existingText}
                onChange={(e) => setExistingText(e.target.value)}
                rows={6}
                data-testid="textarea-existing-text"
              />
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Recipient Type</Label>
              <Select value={aiRecipientType} onValueChange={setAiRecipientType}>
                <SelectTrigger data-testid="select-ai-recipient-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="adjuster">Insurance Adjuster</SelectItem>
                  <SelectItem value="policyholder">Policyholder</SelectItem>
                  <SelectItem value="carrier">Insurance Carrier</SelectItem>
                  <SelectItem value="contractor">Contractor</SelectItem>
                  <SelectItem value="other">Other</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Email Purpose</Label>
              <Select value={aiEmailPurpose} onValueChange={setAiEmailPurpose}>
                <SelectTrigger data-testid="select-ai-email-purpose">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="update">Status Update</SelectItem>
                  <SelectItem value="request_info">Request Information</SelectItem>
                  <SelectItem value="negotiation">Negotiation</SelectItem>
                  <SelectItem value="follow_up">Follow Up</SelectItem>
                  <SelectItem value="general">General Communication</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <Label>Additional Context (Optional)</Label>
            <Textarea
              placeholder="Provide any specific details or points you want to include in the email..."
              value={aiEmailContext}
              onChange={(e) => setAiEmailContext(e.target.value)}
              rows={10}
              data-testid="textarea-ai-context"
            />
          </div>
          <div className="flex space-x-2">
            <Button
              onClick={handleGenerateAIDraft}
              disabled={isGeneratingAIDraft}
              className="flex-1"
              data-testid="button-generate-ai-draft"
            >
              {isGeneratingAIDraft ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Sparkles className="h-4 w-4 mr-2" />
                  Generate Draft
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              data-testid="button-cancel-ai-draft"
            >
              Cancel
            </Button>
          </div>
        </div>
        </DialogPrimitive.Content>
      </DialogPortal>
    </Dialog>
  );
}
